/* ShareCatalogModal — copy link + QR code to share a seller's public catalog
 * webpage (/s/:slug/catalog). Turns the storefront into a top-of-funnel
 * acquisition channel: drop the link in emails, IG bios, or print the QR for
 * trade-show signage. */
import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Copy, Download, Mail, MessageCircle, QrCode, ExternalLink } from "lucide-react";

export default function ShareCatalogModal({ open, onClose, url, sellerName }) {
  const qrRef = useRef(null);
  if (!url) return null;

  const copy = () => {
    navigator.clipboard?.writeText(url)
      .then(() => toast.success("Catalog link copied"))
      .catch(() => toast.error("Could not copy link"));
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${(sellerName || "catalog").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-catalog-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR code downloaded");
  };

  const subject = encodeURIComponent(`${sellerName || "Our"} wholesale catalog`);
  const body = encodeURIComponent(`Browse and order from our catalog here:\n${url}`);
  const mailHref = `mailto:?subject=${subject}&body=${body}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(`Browse our catalog: ${url}`)}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="share-catalog-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-4 h-4"/> Share your catalog
          </DialogTitle>
        </DialogHeader>

        <p className="text-[13px] text-foreground/60 -mt-1">
          Anyone with this link can browse your catalog and place an order — no account needed to browse.
        </p>

        {/* QR */}
        <div className="flex justify-center py-2">
          <div ref={qrRef} className="rounded-2xl border border-border bg-white p-4" data-testid="share-catalog-qr">
            <QRCodeCanvas value={url} size={180} level="M" includeMargin={false}/>
          </div>
        </div>

        {/* URL + copy */}
        <div className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 pl-3 pr-1.5 py-1.5">
          <ExternalLink className="w-3.5 h-3.5 text-foreground/40 shrink-0"/>
          <span className="text-[12.5px] mono truncate flex-1 text-foreground/75" data-testid="share-catalog-url">{url}</span>
          <Button size="sm" onClick={copy} data-testid="share-catalog-copy" className="rounded-lg h-8 px-3 bg-foreground text-background hover:bg-foreground/90">
            <Copy className="w-3.5 h-3.5 mr-1.5"/>Copy
          </Button>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-3 gap-2 pt-1">
          <Button variant="outline" onClick={downloadQR} data-testid="share-catalog-download-qr" className="rounded-xl h-10 flex-col gap-0.5 text-[11px]">
            <Download className="w-4 h-4"/>QR PNG
          </Button>
          <a href={mailHref} data-testid="share-catalog-email" className="inline-flex flex-col items-center justify-center gap-0.5 rounded-xl h-10 border border-border text-[11px] text-foreground/75 hover:border-foreground/40 transition-colors">
            <Mail className="w-4 h-4"/>Email
          </a>
          <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="share-catalog-whatsapp" className="inline-flex flex-col items-center justify-center gap-0.5 rounded-xl h-10 border border-border text-[11px] text-foreground/75 hover:border-foreground/40 transition-colors">
            <MessageCircle className="w-4 h-4"/>WhatsApp
          </a>
        </div>

        <a href={url} target="_blank" rel="noopener noreferrer" data-testid="share-catalog-preview"
          className="text-center text-[12.5px] text-foreground/60 hover:text-foreground underline-offset-2 hover:underline pt-1">
          Preview catalog webpage →
        </a>
      </DialogContent>
    </Dialog>
  );
}
